import { MessageId } from "./message-id";
import { hasValue, isNullish } from "../util/common-util";
import { ListIndex } from "./list-index";
import { t } from "i18next";
import { pathToDisplayablePath, PropertyMeta, ValueObjectMeta } from "../meta";
import { ZodType } from "zod";

export type ValidationStateNotYet = {
  readonly state: "not_yet";
};

export type ValidationStateOk<T> = {
  readonly state: "ok";
  readonly value: T;
};
export type ValidationStateError = {
  readonly state: "error";
  readonly errors: ValidationError[];
};

export type ValidationStateOkOrError<T> =
  | ValidationStateOk<T>
  | ValidationStateError;

export type ValidationState<T> =
  | ValidationStateNotYet
  | ValidationStateOk<T>
  | ValidationStateError;

export type ValidationError = {
  /**
   * オブジェクト物理名。
   */
  readonly objectName: string;
  /**
   * オブジェクト論理名。
   */
  readonly objectLogicalName: string;

  /**
   * フルプロパティ物理名。
   * 例:
   * ユーザーオブジェクト でエラーとなった場合、この値は null となる。
   * ユーザーオブジェクト.birthday でエラーとなった場合、この値は「birthday」となる。
   * ユーザーオブジェクト.birthday.month でエラーとなった場合、この値は「birthday.month」となる。
   */
  readonly fullPropertyName: string | null;
  /**
   * フルプロパティ表示名。
   * 例:
   * ユーザーオブジェクト でエラーとなった場合、この値は null となる。
   * ユーザーオブジェクト.birthday でエラーとなった場合、この値は「誕生日」となる。
   * ユーザーオブジェクト.birthday.month でエラーとなった場合、この値は「誕生日 - 月」となる。
   */
  readonly fullPropertyDisplayName: string | null;
  /**
   * エラーメッセージ。
   * プロパティ名は含まない。
   */
  readonly errorMessage: string;
};

export type DisplayablePath = (MessageId | ListIndex)[];

export type ValidationErrorInternal = {
  readonly path: (string | number)[];
  readonly errorMessageObject: ErrorMessageObject;
};

export function validationErrorFromInternal(
  objectName: string,
  objectLogicalName: string,
  internal: ValidationErrorInternal,
  propertyMetas: Record<
    string,
    | PropertyMeta<ValueObjectMeta<ZodType<any>, ZodType<any>, ZodType<any>>>
    | undefined
  > = {},
): ValidationError {
  const displayablePath = pathToDisplayablePath(internal.path, propertyMetas);
  return {
    objectName,
    objectLogicalName,
    fullPropertyName: internal.path.join("."),
    fullPropertyDisplayName: displayablePathToDisplayValue(displayablePath),
    errorMessage: t(
      internal.errorMessageObject.errorMessageId.value,
      internal.errorMessageObject.errorMessageInterpolations ?? {},
    ),
  };
}

export function displayablePathToDisplayValue(
  path: DisplayablePath | null,
): string | null {
  if (isNullish(path) || path.length <= 0) return null;
  let text = "";
  let lastFragment: MessageId | ListIndex | null = null;
  for (const fragment of path) {
    switch (fragment.brand) {
      case "message_id":
        if (hasValue(lastFragment)) {
          text += " - ";
        }
        text += t(fragment.value);
        break;
      case "list_index":
        text += `(${fragment.value + 1})`; // 1始まりにしたいので、1加算している。
        break;
    }

    lastFragment = fragment;
  }
  return text;
}

export type ErrorMessageObject = {
  readonly errorMessageId: MessageId;
  readonly errorMessageInterpolations?: Record<string, any>;
};

/**
 * 指定したパス（フルプロパティ名）のエラーを除外する。
 * 任意のインデクスにマッチさせるには、 "#"を用いる。
 * 例えば、 mailAddresses.#.domain は、mailAddresses.0.domain, mailAddresses.1.domain 等にマッチするため、それらを除外する。
 */
export function filterOutErrorsOfPaths(
  validationErrors: ValidationError[],
  paths: string[],
): ValidationError[] {
  return validationErrors.filter((e) => !paths.some((p) => matchToPath(e, p)));
}
/**
 * 指定したパス（フルプロパティ名）のエラーの論理名を差し替える。
 * 任意のインデクスにマッチさせるには、 "#"を用いる。
 * 例えば、 mailAddresses.#.domain は、mailAddresses.0.domain, mailAddresses.1.domain 等にマッチする。
 */
export function renameErrorsOfPaths(
  validationErrors: ValidationError[],
  renames: {
    path: string;
    objectLogicalName?: string;
    fullPropertyDisplayName?: string;
  }[],
): ValidationError[] {
  return validationErrors.map((e) => {
    const foundRename = renames.find((rn) => matchToPath(e, rn.path));
    if (isNullish(foundRename)) return e;
    return {
      ...e,
      objectLogicalName: foundRename.objectLogicalName ?? e.objectLogicalName,
      fullPropertyDisplayName:
        foundRename.fullPropertyDisplayName ?? e.fullPropertyDisplayName,
    };
  });
}

const matchToPathRegex = /\.\d+\./;
function matchToPath(e: ValidationError, path: string): boolean {
  const errorPath = e.fullPropertyName;
  if (isNullish(errorPath)) return false;
  if (errorPath === path) return true;
  return errorPath.replace(matchToPathRegex, ".#.") === path;
}
